/**
 * Materiais por especialista de IA — **API Adm**, catálogo GLOBAL (sem `idCliente`),
 * por isso tudo usa `chaveGlobal`, nunca `chaveDeQuery` — mesmo raciocínio de
 * `administracao.ts` (grupos, clientes: configuração de produto, não dado de cliente).
 *
 * Arquivo separado de `administracao.ts` por domínio (a convenção do projeto não exige
 * um arquivo por módulo — `administracao.ts` já mistura clientes/usuários/grupos — mas
 * este é grande o bastante, com upload próprio, para ganhar o seu).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { apiAdm } from '@/api/clients/apiAdm'
import {
  materialSchema,
  urlDeDownloadDeMaterialSchema,
  urlDeUploadDeMaterialSchema,
  type EspecialistaIa,
  type Material,
  type NovoMaterial,
  type PatchDeMaterial,
} from '@/api/contracts/materiais'
import { enviarArquivo } from '@/api/upload'

import { chaveGlobal } from './chaveDeQuery'

export function useMateriais(especialista: EspecialistaIa) {
  return useQuery({
    queryKey: chaveGlobal('adm-materiais', especialista),
    queryFn: async () =>
      z
        .object({ itens: z.array(materialSchema) })
        .parse(await apiAdm.get<unknown>('/materiais', { params: { especialista } })).itens,
  })
}

/** Invalida TODAS as listas de materiais, não só a do especialista afetado — a chave
 * leva o especialista por último, então o prefixo `chaveGlobal('adm-materiais')`
 * casa com as três (mesmo padrão de `invalidarCatalogo` em `compliance.ts`). */
function invalidarMateriais(client: ReturnType<typeof useQueryClient>) {
  return client.invalidateQueries({ queryKey: chaveGlobal('adm-materiais') })
}

export function useCriarMaterial() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (dados: NovoMaterial): Promise<Material> => {
      if (dados.formato === 'texto') {
        return materialSchema.parse(
          await apiAdm.post<unknown>('/materiais', {
            especialista: dados.especialista,
            titulo: dados.titulo,
            formato: 'texto',
            conteudo: dados.conteudo,
          }),
        )
      }
      const enviado = await enviarArquivo(dados.arquivo, async (meta) =>
        urlDeUploadDeMaterialSchema.parse(
          await apiAdm.post<unknown>('/materiais/upload-url', {
            especialista: dados.especialista,
            ...meta,
          }),
        ),
      )
      return materialSchema.parse(
        await apiAdm.post<unknown>('/materiais', {
          especialista: dados.especialista,
          titulo: dados.titulo,
          formato: enviado.tipo,
          chave: enviado.chave,
        }),
      )
    },
    onSuccess: () => void invalidarMateriais(client),
  })
}

export function useAtualizarMaterial() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (params: { id: string; patch: PatchDeMaterial }) =>
      materialSchema.parse(await apiAdm.patch<unknown>(`/materiais/${params.id}`, params.patch)),
    onSuccess: () => void invalidarMateriais(client),
  })
}

export function useExcluirMaterial() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => apiAdm.delete<unknown>(`/materiais/${id}`),
    onSuccess: () => void invalidarMateriais(client),
  })
}

/**
 * A URL de leitura, pedida NA HORA do clique. Nunca cacheada — mesmo raciocínio de
 * `obterUrlDeDownload` em `compliance.ts`: expira em minutos, e uma URL assinada
 * guardada no cache do TanStack Query abriria um link morto mais cedo ou mais tarde.
 */
export async function obterUrlDeDownloadDeMaterial(id: string): Promise<string> {
  return urlDeDownloadDeMaterialSchema.parse(
    await apiAdm.get<unknown>(`/materiais/${id}/download-url`),
  ).url
}
